import { createReducer, on } from '@ngrx/store';
import { initialAuthState, AuthState, TokenState, initialTokenState } from './auth.state';
import * as AuthActions from './auth.actions';
import { UserAuthentication, UserToken } from '@core/models/domain/auth.domain';

// Minimal session shape rebuilt from a /refresh result. /refresh echoes the account IDs carried
// on the refresh cookie, so a session can be restored even when the in-memory state was lost.
export const buildAuthenticationFromToken = (
    token: UserToken,
    fallbackUserAccountId: string = '',
    fallbackSchoolAccountId: string = ''
): UserAuthentication => ({
    userAccountId: token.userAccountId || fallbackUserAccountId,
    schoolAccountId: token.schoolAccountId || fallbackSchoolAccountId,
    token: token.token,
    isAdmin: true,
    id: '',
    type: null,
    e164phoneNumber: null,
    emailAddress: null,
    loggingHistories: [],
    creationDatetime: new Date(),
    lastModifiedDatetime: new Date()
});

export const authReducer = createReducer<AuthState>(
  initialAuthState,

  // LOGIN
  on(AuthActions.login, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.loginSuccess, (state, { authentication }) => ({
      ...state,
      loading: false,
      authentication
  })),
  on(AuthActions.loginFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
  })),

  // LOGOUT
  // Preserve isHydrated: the startup hydration attempt has already completed and
  // does not run again without a page reload. Resetting it to false would leave
  // AuthGuard waiting forever after a re-login (it blocks until isHydrated is true).
  on(AuthActions.logout, (state) => ({
      ...initialAuthState,
      isHydrated: state.isHydrated
  })),

  // REFRESH TOKEN
  on(AuthActions.refreshToken, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.refreshTokenSuccess, (state, { token }) => ({
      ...state,
      loading: false,
      // A successful refresh must never be discarded: when the in-memory authentication was
      // lost (failed hydration, prior refresh failure), rebuild it from the refresh result.
      authentication: state.authentication
        ? { ...state.authentication, token: token.token }
        : buildAuthenticationFromToken(token)
  })),
  // Preserve isHydrated like logout does: the startup hydration does not run again without a
  // page reload, and AuthGuard blocks navigation until it is true.
  on(AuthActions.refreshTokenFailure, (state, { error }) => ({
      ...initialAuthState,
      isHydrated: state.isHydrated,
      error
  })),

  // HYDRATE
  on(AuthActions.hydrateAuthSuccess, (state, { authentication }) => ({
      ...state,
      authentication,
      loading: false,
      error: null,
      isHydrated: true
  })),
  on(AuthActions.hydrateAuthFailure, (state) => ({
      ...state,
      authentication: null,
      loading: false,
      isHydrated: true
  }))
)

export const tokenReducer = createReducer<TokenState>(
  initialTokenState,

  // CREATE SCHOOL ACCOUNT
  on(AuthActions.createSchoolAccount, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.createSchoolAccountSuccess, (state, { token }) => ({
      ...state,
      loading: false,
      token,
  })),
  on(AuthActions.createSchoolAccountFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
  })),

  // RESEND CODE
  on(AuthActions.resendValidationCode, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.resendValidationCodeSuccess, (state, { token }) => ({
      ...state,
      loading: false,
      token
  })),
  on(AuthActions.resendValidationCodeFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error,
  })),

  // VALIDATE CODE
  on(AuthActions.validateCode, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.validateCodeSuccess, (state, { token }) => ({
      ...state,
      loading: false,
      token
  })),
  on(AuthActions.validateCodeFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
  })),

  // RESET PASSWORD
  on(AuthActions.requestPasswordReset, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.requestPasswordResetSuccess, (state, { token }) => ({
      ...state,
      loading: false,
      token
  })),
  on(AuthActions.requestPasswordResetFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
  })),

  // SET PASSWORD
  on(AuthActions.setPassword, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.setPasswordSuccess, (state) => ({
      ...state,
      loading: false
  })),
  on(AuthActions.setPasswordFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
  })),

  // CHANGE PASSWORD
  on(AuthActions.changePassword, (state) => ({
      ...state,
      loading: true,
      error: null
  })),
  on(AuthActions.changePasswordSuccess, (state) => ({
      ...state,
      loading: false
  })),
  on(AuthActions.changePasswordFailure, (state, { error }) => ({
      ...state,
      loading: false,
      error
  })),

  // LOGOUT
  on(AuthActions.logout, (state) => ({
      ...initialTokenState
  }))
);
